import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { CheckCircle2, RefreshCw, Settings2, Plug, Clock, Database, AlertCircle, Trash2, TestTube, X, Save, Users, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRM_ICONS = {
  qhare: { color: 'from-blue-500 to-indigo-600', border: 'border-blue-500/30', bg: 'from-blue-500/5 to-indigo-500/5' },
  sania: { color: 'from-amber-500 to-orange-600', border: 'border-amber-500/30', bg: 'from-amber-500/5 to-orange-500/5' },
  hubspot: { color: 'from-orange-500 to-red-500', border: 'border-orange-500/30', bg: 'from-orange-500/5 to-red-500/5' },
  pipedrive: { color: 'from-green-500 to-emerald-600', border: 'border-green-500/30', bg: 'from-green-500/5 to-emerald-500/5' },
};


function CrmAuthorMapping({ source, token, persons, onRefresh }) {
  const [authors, setAuthors] = useState([]);
  const [totalFallback, setTotalFallback] = useState(0);
  const [totalNoAuthor, setTotalNoAuthor] = useState(0);
  const [mapping, setMapping] = useState(source?.user_mapping || {});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [redistributing, setRedistributing] = useState(false);
  const [redistributeResult, setRedistributeResult] = useState(null);

  const fetchAuthors = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/crm-sources/${source.id}/detected-authors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuthors(data.authors || []);
      setTotalFallback(data.total_in_fallback || 0);
      setTotalNoAuthor(data.total_no_author || 0);
    } catch (err) {
      toast.error('Erreur chargement auteurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (source?.id) {
      fetchAuthors();
      // Resume polling if a redistribution is already in progress
      if (source?.redistribute_status === 'running' || source?.redistribute_status === 'pending') {
        setRedistributing(true);
        setRedistributeResult(source?.redistribute_progress);
        pollRedistributeStatus();
      }
    }
  }, [source?.id]);

  const handleMappingChange = (authorName, personId) => {
    setMapping(prev => {
      const copy = { ...prev };
      if (personId) {
        copy[authorName] = personId;
      } else {
        delete copy[authorName];
      }
      return copy;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/crm-sources/${source.id}`, { user_mapping: mapping }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Correspondances sauvegardees');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleRedistribute = async () => {
    if (!window.confirm(`Redistribuer les commentaires CRM vers les clones mappes ?\nCette operation se lance en arriere-plan.`)) return;
    setRedistributing(true);
    setRedistributeResult(null);
    try {
      await axios.post(`${API}/crm-sources/${source.id}/redistribute`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Redistribution lancee en arriere-plan');
      pollRedistributeStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
      setRedistributing(false);
    }
  };

  const pollRedistributeStatus = () => {
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/crm-sources/${source.id}/redistribute-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const progress = data.progress;
        setRedistributeResult(progress);

        if (data.status === 'completed') {
          clearInterval(interval);
          setRedistributing(false);
          toast.success(`Redistribution terminee : ${progress?.moved || 0} deplaces`);
          fetchAuthors();
          onRefresh();
        } else if (data.status === 'failed') {
          clearInterval(interval);
          setRedistributing(false);
          toast.error('Redistribution echouee');
        }
      } catch {
        clearInterval(interval);
        setRedistributing(false);
      }
    }, 2000);
  };

  const personName = (pid) => {
    const p = (persons || []).find(x => x.id === pid);
    return p ? (p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim()) : '';
  };

  const mappedCount = authors.filter(a => mapping[a.name]).reduce((s, a) => s + a.count, 0);
  const unmappedCount = totalFallback - mappedCount;
  const hasChanges = JSON.stringify(mapping) !== JSON.stringify(source?.user_mapping || {});

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-xs text-muted-foreground">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement des auteurs...
      </div>
    );
  }

  if (!authors.length) return null;

  return (
    <div className="space-y-3" data-testid="crm-author-mapping">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" /> Correspondance Auteurs CRM
        </Label>
        <div className="flex items-center gap-2">
          {totalFallback > 0 && (
            <Badge variant="outline" className="text-xs">
              {totalFallback} dans le clone par defaut
            </Badge>
          )}
        </div>
      </div>

      <div className="bg-muted/30 rounded-lg border p-3 space-y-2">
        {authors.map(({ name: authorName, count }) => (
          <div key={authorName} className="flex items-center gap-2" data-testid={`author-row-${authorName}`}>
            <div className="flex items-center gap-1.5 min-w-[140px]">
              <Badge variant="secondary" className="text-xs font-mono">{authorName}</Badge>
              <span className="text-[10px] text-muted-foreground">({count})</span>
            </div>
            <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
            <select
              value={mapping[authorName] || ''}
              onChange={e => handleMappingChange(authorName, e.target.value)}
              className="h-7 text-xs border rounded px-2 bg-background flex-1"
              data-testid={`author-select-${authorName}`}
            >
              <option value="">Non rattache</option>
              {(persons || []).map(p => (
                <option key={p.id} value={p.id}>
                  {p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim()}
                </option>
              ))}
            </select>
            {mapping[authorName] && (
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            )}
          </div>
        ))}

        {totalNoAuthor > 0 && (
          <div className="flex items-center gap-2 pt-1 border-t border-muted">
            <span className="text-[10px] text-muted-foreground italic">
              {totalNoAuthor} commentaires sans auteur (resteront dans le clone par defaut)
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="gap-1.5 h-7 text-xs"
          data-testid="crm-save-mapping"
        >
          <Save className="w-3.5 h-3.5" />
          {saving ? 'Enregistrement...' : 'Sauvegarder'}
        </Button>

        <Button
          size="sm"
          variant="outline"
          onClick={handleRedistribute}
          disabled={redistributing || Object.keys(mapping).length === 0}
          className="gap-1.5 h-7 text-xs"
          data-testid="crm-redistribute"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${redistributing ? 'animate-spin' : ''}`} />
          {redistributing ? 'Redistribution...' : 'Synchroniser la base'}
        </Button>

        {redistributeResult && (
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            {redistributing && redistributeResult.total > 0 ? (
              <div className="flex-1 space-y-1">
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>{redistributeResult.processed || 0} / {redistributeResult.total}</span>
                  <span>{redistributeResult.moved || 0} deplaces</span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.round(((redistributeResult.processed || 0) / redistributeResult.total) * 100)}%` }}
                  />
                </div>
              </div>
            ) : !redistributing && redistributeResult.moved !== undefined ? (
              <span className="text-xs text-emerald-600">
                {redistributeResult.moved} deplaces, {redistributeResult.skipped} ignores
              </span>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function CrmConfigForm({ type, source, token, onSaved, onCancel, persons }) {
  const isEdit = !!source;
  const catalogDefaults = { qhare: 'https://qhare.fr/api', sania: '' };

  const [baseUrl, setBaseUrl] = useState(source?.base_url || catalogDefaults[type] || '');
  const [apiKey, setApiKey] = useState('');
  const [name, setName] = useState(source?.name || type.toUpperCase());
  const [syncMode, setSyncMode] = useState(source?.sync_mode || 'enrich_only');
  const [defaultPersonId, setDefaultPersonId] = useState(source?.default_person_id || '');
  const [userMapping, setUserMapping] = useState(source?.user_mapping || {});
  const [mappingKey, setMappingKey] = useState('');
  const [mappingPersonId, setMappingPersonId] = useState('');
  const [showAdvancedMapping, setShowAdvancedMapping] = useState(Object.keys(source?.user_mapping || {}).length > 0);
  const [saving, setSaving] = useState(false);

  const addMapping = () => {
    if (!mappingKey.trim() || !mappingPersonId) return;
    setUserMapping(prev => ({ ...prev, [mappingKey.trim()]: mappingPersonId }));
    setMappingKey('');
    setMappingPersonId('');
  };

  const removeMapping = (key) => {
    setUserMapping(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const handleSave = async () => {
    if (!baseUrl && type !== 'sania') { toast.error('URL de base requise'); return; }
    if (!apiKey && !isEdit) { toast.error('Cle API requise'); return; }

    setSaving(true);
    try {
      if (isEdit) {
        const payload = { name, base_url: baseUrl, user_mapping: userMapping, sync_mode: syncMode, default_person_id: defaultPersonId || null };
        if (apiKey) payload.api_key = apiKey;
        await axios.put(`${API}/crm-sources/${source.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Source CRM mise a jour');
      } else {
        await axios.post(`${API}/crm-sources`, {
          type, name, base_url: baseUrl, api_key: apiKey, sync_mode: syncMode, user_mapping: userMapping, default_person_id: defaultPersonId || null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Source CRM creee');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const personName = (pid) => {
    const p = (persons || []).find(x => x.id === pid);
    return p ? (p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || pid.slice(0, 8)) : pid.slice(0, 8);
  };

  return (
    <div className="space-y-4 pt-2" data-testid={`crm-config-form-${type}`}>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Nom</Label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Mon CRM" className="h-8 text-sm" />
        </div>
        <div>
          <Label className="text-xs">URL de base</Label>
          <Input value={baseUrl} onChange={e => setBaseUrl(e.target.value)} placeholder="https://..." className="h-8 text-sm" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Cle API {isEdit && '(laisser vide pour garder l\'actuelle)'}</Label>
        <Input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder={isEdit ? '********' : 'Votre cle API'} className="h-8 text-sm" />
      </div>

      <div>
        <Label className="text-xs font-medium mb-1.5 block">Mode de synchronisation</Label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setSyncMode('enrich_only')}
            className={`p-2.5 rounded-lg border text-left transition-all ${syncMode === 'enrich_only'
              ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
              : 'border-muted hover:border-muted-foreground/30'}`}
            data-testid="sync-mode-enrich"
          >
            <div className="text-xs font-medium">Enrichissement seul</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">Met a jour les contacts existants uniquement</div>
          </button>
          <button
            type="button"
            onClick={() => setSyncMode('full_import')}
            className={`p-2.5 rounded-lg border text-left transition-all ${syncMode === 'full_import'
              ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
              : 'border-muted hover:border-muted-foreground/30'}`}
            data-testid="sync-mode-full"
          >
            <div className="text-xs font-medium">Import complet</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">Cree les contacts manquants + enrichit</div>
          </button>
        </div>
      </div>

      <Separator />
      <div>
        <Label className="text-xs font-medium flex items-center gap-1.5 mb-2">
          <Users className="w-3.5 h-3.5" /> Clone par defaut
        </Label>
        <p className="text-xs text-muted-foreground mb-2">Tous les commentaires CRM seront rattaches a ce clone</p>
        <select
          value={defaultPersonId}
          onChange={e => setDefaultPersonId(e.target.value)}
          className="h-8 text-xs border rounded px-2 bg-background w-full"
          data-testid="crm-default-clone"
        >
          <option value="">Aucun (ignorer les commentaires non mappes)</option>
          {(persons || []).map(p => (
            <option key={p.id} value={p.id}>{p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.id.slice(0, 8)}</option>
          ))}
        </select>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setShowAdvancedMapping(!showAdvancedMapping)}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
        >
          {showAdvancedMapping ? '▾' : '▸'} Mapping avance par commercial (optionnel)
        </button>

        {showAdvancedMapping && (
          <div className="mt-2 pl-2 border-l-2 border-muted">
            <p className="text-[10px] text-muted-foreground mb-2">Associer un nom/code CRM a un clone specifique (prioritaire sur le clone par defaut)</p>

            {Object.entries(userMapping).map(([key, pid]) => (
              <div key={key} className="flex items-center gap-2 mb-1.5">
                <Badge variant="secondary" className="text-xs">{key}</Badge>
                <span className="text-xs text-muted-foreground">→</span>
                <span className="text-xs font-medium">{personName(pid)}</span>
                <button onClick={() => removeMapping(key)} className="ml-auto text-muted-foreground hover:text-destructive">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}

            <div className="flex gap-2 mt-2">
              <Input value={mappingKey} onChange={e => setMappingKey(e.target.value)} placeholder="Nom CRM (ex: SDI)" className="h-7 text-xs flex-1" />
              <select
                value={mappingPersonId}
                onChange={e => setMappingPersonId(e.target.value)}
                className="h-7 text-xs border rounded px-2 bg-background flex-1"
              >
                <option value="">Choisir un clone...</option>
                {(persons || []).map(p => (
                  <option key={p.id} value={p.id}>{p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.id.slice(0, 8)}</option>
                ))}
              </select>
              <Button size="sm" variant="outline" onClick={addMapping} disabled={!mappingKey || !mappingPersonId} className="h-7 text-xs px-2">+</Button>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2 pt-2">
        <Button onClick={handleSave} disabled={saving} size="sm" className="gap-1.5" data-testid={`crm-save-${type}`}>
          <Save className="w-3.5 h-3.5" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>Annuler</Button>
      </div>
    </div>
  );
}


function CrmCard({ catalogEntry, source, token, onRefresh, persons }) {
  const { type, name: catalogName } = catalogEntry;
  const style = CRM_ICONS[type] || CRM_ICONS.qhare;
  const [configuring, setConfiguring] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toggling, setToggling] = useState(false);

  const isConnected = !!source;
  const isEnabled = source?.enabled;

  const handleTest = async () => {
    if (!source) return;
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/crm-sources/${source.id}/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.success) {
        toast.success(`Connexion OK${data.total_leads ? ` — ${data.total_leads} leads` : ''}`);
      } else {
        toast.error(`Echec : ${data.error}`);
      }
    } catch { toast.error('Erreur de test'); }
    finally { setTesting(false); }
  };

  const handleSync = async () => {
    if (!source) return;
    setSyncing(true);
    try {
      await axios.post(`${API}/crm-sources/${source.id}/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Synchronisation lancee');
      setTimeout(onRefresh, 5000);
      setTimeout(onRefresh, 15000);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSyncing(false); }
  };

  const handleToggle = async (enabled) => {
    if (!source) return;
    setToggling(true);
    try {
      await axios.put(`${API}/crm-sources/${source.id}`, { enabled }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(enabled ? 'Source CRM activee' : 'Source CRM desactivee');
      onRefresh();
    } catch { toast.error('Erreur'); }
    finally { setToggling(false); }
  };

  const handleDelete = async () => {
    if (!source || !window.confirm('Supprimer cette source CRM ?')) return;
    try {
      await axios.delete(`${API}/crm-sources/${source.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Source CRM supprimee');
      onRefresh();
    } catch { toast.error('Erreur de suppression'); }
  };

  return (
    <Card data-testid={`crm-card-${type}`} className={`${style.border} bg-gradient-to-br ${style.bg}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${style.color} flex items-center justify-center shrink-0`}>
            <Database className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base flex items-center gap-2">
              {source?.name || catalogName}
              {isConnected ? (
                <Badge variant="outline" className={`text-xs gap-1 ${isEnabled ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' : 'bg-muted text-muted-foreground'}`}>
                  {isEnabled ? <><CheckCircle2 className="w-3 h-3" /> Actif</> : 'Inactif'}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs text-muted-foreground">Non connecte</Badge>
              )}
            </CardTitle>
            <CardDescription className="text-xs">CRM {catalogName}</CardDescription>
          </div>
          {isConnected && (
            <div className="flex items-center gap-1.5">
              <Switch
                checked={isEnabled}
                onCheckedChange={handleToggle}
                disabled={toggling}
                data-testid={`crm-toggle-${type}`}
              />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isConnected && !configuring && (
          <>
            {source.last_sync_at && (
              <div className="flex items-center gap-3 text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2" data-testid={`crm-sync-status-${type}`}>
                <Clock className="w-3.5 h-3.5 shrink-0" />
                <span>Derniere sync : {new Date(source.last_sync_at).toLocaleString('fr-FR')}</span>
                {source.leads_synced_count > 0 && <span>{source.leads_synced_count} leads</span>}
                {source.comments_synced_count > 0 && <span>{source.comments_synced_count} notes</span>}
                {source.last_sync_status === 'syncing' && (
                  <Badge variant="outline" className="text-amber-600 border-amber-400 text-xs">En cours...</Badge>
                )}
                {source.last_sync_status === 'failed' && (
                  <Badge variant="outline" className="text-red-600 border-red-400 text-xs gap-1">
                    <AlertCircle className="w-3 h-3" /> Erreur
                  </Badge>
                )}
              </div>
            )}
            {source.last_sync_error && (
              <p className="text-xs text-red-500 bg-red-50 dark:bg-red-950/20 rounded px-2 py-1">{source.last_sync_error}</p>
            )}
            {Object.keys(source.user_mapping || {}).length > 0 && (
              <div className="text-xs text-muted-foreground">
                <span className="font-medium">{Object.keys(source.user_mapping).length} mappings</span> utilisateur → clone
                {source.sync_mode && (
                  <span className="ml-2">| Mode : <span className="font-medium">{source.sync_mode === 'full_import' ? 'Import complet' : 'Enrichissement'}</span></span>
                )}
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing || !isEnabled} className="gap-1.5 h-7 text-xs" data-testid={`crm-sync-${type}`}>
                <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Sync...' : 'Synchroniser'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1.5 h-7 text-xs" data-testid={`crm-test-${type}`}>
                <TestTube className={`w-3.5 h-3.5 ${testing ? 'animate-pulse' : ''}`} /> Tester
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfiguring(true)} className="gap-1.5 h-7 text-xs">
                <Settings2 className="w-3.5 h-3.5" /> Modifier
              </Button>
              <Button variant="ghost" size="sm" onClick={handleDelete} className="gap-1.5 h-7 text-xs text-destructive hover:text-destructive">
                <Trash2 className="w-3.5 h-3.5" /> Supprimer
              </Button>
            </div>

            <Separator />
            <CrmAuthorMapping source={source} token={token} persons={persons} onRefresh={onRefresh} />
          </>
        )}

        {!isConnected && !configuring && (
          <Button variant="outline" size="sm" onClick={() => setConfiguring(true)} className="gap-1.5" data-testid={`crm-configure-${type}`}>
            <Plug className="w-4 h-4" /> Configurer
          </Button>
        )}

        {configuring && (
          <CrmConfigForm
            type={type}
            source={source}
            token={token}
            persons={persons}
            onSaved={() => { setConfiguring(false); onRefresh(); }}
            onCancel={() => setConfiguring(false)}
          />
        )}
      </CardContent>
    </Card>
  );
}


export function CrmSection({ token }) {
  const [catalog, setCatalog] = useState([]);
  const [sources, setSources] = useState([]);
  const [persons, setPersons] = useState([]);

  const fetchData = async () => {
    try {
      const [catRes, srcRes, persRes] = await Promise.all([
        axios.get(`${API}/crm-sources/catalog`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/crm-sources`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/persons`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setCatalog(catRes.data);
      setSources(srcRes.data);
      setPersons(Array.isArray(persRes.data) ? persRes.data : persRes.data.persons || []);
    } catch (err) {
      console.error('CRM fetch error:', err);
    }
  };

  useEffect(() => { fetchData(); }, [token]);

  // Future CRMs (not yet available)
  const futureCrms = [
    { type: 'hubspot', name: 'HubSpot' },
    { type: 'pipedrive', name: 'Pipedrive' },
  ];

  return (
    <div className="space-y-4" data-testid="crm-section">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Integrations CRM</h3>
      <div className="grid gap-4 md:grid-cols-2">
        {catalog.map(entry => {
          const source = sources.find(s => s.type === entry.type);
          return (
            <CrmCard
              key={entry.type}
              catalogEntry={entry}
              source={source}
              token={token}
              onRefresh={fetchData}
              persons={persons}
            />
          );
        })}
        {futureCrms.map(entry => (
          <Card key={entry.type} data-testid={`crm-card-${entry.type}`} className="opacity-50 border-dashed">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${(CRM_ICONS[entry.type] || CRM_ICONS.qhare).color} flex items-center justify-center opacity-50`}>
                  <Database className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-base text-muted-foreground">{entry.name}</CardTitle>
                  <CardDescription className="text-xs">Bientot disponible</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>
    </div>
  );
}
